import React, { useCallback } from "react";
import { Badge, Popover, Spin, Button, Modal } from "antd";
import { BellOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  useGetAdminNotificationsQuery,
  useMarkAdminNotificationsReadMutation,
  useDeleteAdminNotificationMutation,
} from "@services/notificationApi";
import { useAdminNotificationsSocket } from "../../hooks/useAdminNotificationsSocket";
import type { AdminNotificationItem } from "interface/notification";
import { formatDateTime } from "@utils/dateFormat";

const NOTIFICATION_CLEAR_DELAY_MS = 3000;

function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("authToken");
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = React.useState(false);
  const [allNotificationsModalOpen, setAllNotificationsModalOpen] =
    React.useState(false);
  const clearTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const token = getAuthToken();

  const { data, isLoading, isError, refetch } = useGetAdminNotificationsQuery(
    { unread_only: false },
    { refetchOnMountOrArgChange: true }
  );
  const [markAsRead] = useMarkAdminNotificationsReadMutation();
  const [deleteNotification] = useDeleteAdminNotificationMutation();

  useAdminNotificationsSocket(token, {
    onNotification: refetch,
  });

  const list: AdminNotificationItem[] = data?.data?.results ?? [];
  const totalUnreadCount = list.filter((item) => !item.read).length;

  React.useEffect(() => {
    return () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, []);

  const handleOpenChange = useCallback(
    (visible: boolean) => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }
      setOpen(visible);
      if (visible && totalUnreadCount > 0) {
        markAsRead()
          .then(() => {
            clearTimerRef.current = setTimeout(() => {
              clearTimerRef.current = null;
              refetch();
            }, NOTIFICATION_CLEAR_DELAY_MS);
          })
          .catch(() => {});
      }
    },
    [totalUnreadCount, markAsRead, refetch]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      deleteNotification(id).then(() => refetch()).catch(() => {});
    },
    [deleteNotification, refetch]
  );

  const hasAnyNotifications = list.length > 0;
  const showEmpty = !isLoading && (isError || !hasAnyNotifications);

  const content = (
    <div className="min-w-[280px] max-w-[360px]">
      <div className="flex items-center justify-between border-b border-gray-200 px-2 py-2 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-800 dark:text-white">
          Notifications
        </span>
        {totalUnreadCount > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {totalUnreadCount} unread
          </span>
        )}
      </div>
      <div className="notification-panel-scroll max-h-[320px] overflow-y-auto py-1">
        {isLoading && (
          <div className="flex justify-center py-6">
            <Spin size="small" />
          </div>
        )}
        {showEmpty && (
          <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No notifications
          </div>
        )}
        {!isLoading && !isError && list.length > 0 && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {list.map((item) => {
              const id = item.id ?? item._id ?? "";
              const title = item.title ?? "Notification";
              const message = item.message ?? "";
              const isUnread = !item.read;
              const createdAt = item.createdAt
                ? formatDateTime(item.createdAt)
                : "";
              return (
                <li
                  key={id}
                  className={`group flex items-start gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${isUnread ? "bg-blue-50/50 dark:bg-blue-900/10" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {title}
                    </div>
                    {message && (
                      <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">
                        {message}
                      </div>
                    )}
                    {createdAt && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {createdAt}
                      </div>
                    )}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined className="!text-xs" />}
                    className="!p-1 opacity-70 group-hover:opacity-100 shrink-0"
                    aria-label="Delete notification"
                    onClick={(e) => handleDelete(e, id)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="border-t border-gray-200 px-3 py-2 dark:border-gray-700">
        <button
          type="button"
          className="text-xs font-medium text-[#0056D2] hover:underline"
          onClick={() => {
            setOpen(false);
            setAllNotificationsModalOpen(true);
          }}
        >
          View all notifications →
        </button>
      </div>
    </div>
  );

  const allNotificationsModal = (
    <Modal
      title="All Notifications"
      open={allNotificationsModalOpen}
      onCancel={() => setAllNotificationsModalOpen(false)}
      footer={null}
      width={520}
      destroyOnHidden
      className="notification-all-modal"
    >
      <div className="notification-panel-scroll max-h-[60vh] overflow-y-auto">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        )}
        {!isLoading && (isError || list.length === 0) && (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            No notifications
          </div>
        )}
        {!isLoading && !isError && list.length > 0 && (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {list.map((item) => {
              const id = item.id ?? item._id ?? "";
              const title = item.title ?? "Notification";
              const message = item.message ?? "";
              const isUnread = !item.read;
              const createdAt = item.createdAt
                ? formatDateTime(item.createdAt)
                : "";
              return (
                <li
                  key={id}
                  className={`group flex items-start gap-3 py-3 first:pt-0 ${isUnread ? "bg-blue-50/50 dark:bg-blue-900/10 rounded px-2" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {title}
                    </div>
                    {message && (
                      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 break-words">
                        {message}
                      </div>
                    )}
                    {createdAt && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {createdAt}
                      </div>
                    )}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className="!p-1 shrink-0 opacity-70 group-hover:opacity-100"
                    aria-label="Delete notification"
                    onClick={(e) => {
                      handleDelete(e, id);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );

  return (
    <>
      <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      overlayClassName="notification-popover"
    >
      <button
        type="button"
        className="relative flex items-center justify-center w-10 h-10 text-white rounded-lg hover:bg-white/10 transition-colors lg:w-11 lg:h-11"
        aria-label="Notifications"
      >
        <Badge
          count={totalUnreadCount}
          size="small"
          offset={[-2, 2]}
          overflowCount={99}
          color="#0156d2"
          className="[&_.ant-badge-count]:!bg-[#0156d2] [&_.ant-badge-count]:!text-white [&_.ant-scroll-number]:!text-white"
        >
          <BellOutlined className="text-xl !text-white" />
        </Badge>
      </button>
    </Popover>
      {allNotificationsModal}
    </>
  );
};

export default NotificationBell;
